import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Assignment,
  Groups as GroupsIcon,
  School,
  Schedule,
  WorkspacePremium
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';
import { ROLE_DISPLAY_NAMES } from '../../constants/roles';

function nameOf(e) {
  return `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email || '—';
}

function roleLabel(role) {
  if (!role) return 'Other';
  return ROLE_DISPLAY_NAMES[role] || String(role).replace(/_/g, ' ');
}

/** Stable department color themes for boxes + matching role cards */
const DEPARTMENT_THEMES = {
  OPERATIONS: { border: '#1565c0', bg: '#e3f2fd', card: '#bbdefb', header: '#0d47a1' },
  SALES: { border: '#6a1b9a', bg: '#f3e5f5', card: '#e1bee7', header: '#4a148c' },
  PLANNING: { border: '#00695c', bg: '#e0f2f1', card: '#b2dfdb', header: '#004d40' },
  CAMO: { border: '#ef6c00', bg: '#fff3e0', card: '#ffe0b2', header: '#e65100' },
  CAMO_QM: { border: '#f57c00', bg: '#fff8e1', card: '#ffecb3', header: '#ff6f00' },
  MRO: { border: '#c62828', bg: '#ffebee', card: '#ffcdd2', header: '#b71c1c' },
  MRO_QM: { border: '#ad1457', bg: '#fce4ec', card: '#f8bbd0', header: '#880e4f' },
  STORE: { border: '#455a64', bg: '#eceff1', card: '#cfd8dc', header: '#263238' },
  SAFETY: { border: '#2e7d32', bg: '#e8f5e9', card: '#c8e6c9', header: '#1b5e20' },
  MIS: { border: '#4527a0', bg: '#ede7f6', card: '#d1c4e9', header: '#311b92' },
  HR: { border: '#00838f', bg: '#e0f7fa', card: '#b2ebf2', header: '#006064' }
};

const FALLBACK_THEMES = [
  { border: '#5d4037', bg: '#efebe9', card: '#d7ccc8', header: '#3e2723' },
  { border: '#0277bd', bg: '#e1f5fe', card: '#b3e5fc', header: '#01579b' },
  { border: '#558b2f', bg: '#f1f8e9', card: '#dcedc8', header: '#33691e' },
  { border: '#6d4c41', bg: '#fbe9e7', card: '#ffccbc', header: '#bf360c' }
];

function themeForDepartment(department) {
  if (department && DEPARTMENT_THEMES[department]) return DEPARTMENT_THEMES[department];
  const key = department || 'OTHER';
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash + key.charCodeAt(i) * (i + 1)) % 997;
  return FALLBACK_THEMES[hash % FALLBACK_THEMES.length];
}

/**
 * HR employee grid — role-wise boxes with multi-select and bulk assign
 * for shifts, groups, bands, trainings, and group tasks.
 */
function EmployeesGrid({
  employees = [],
  shifts = [],
  canAssign = false,
  canManageBands = false,
  canManageGroups = false,
  canAssignTraining = false,
  defaultFrom,
  onChanged
}) {
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [bands, setBands] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [action, setAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const [shiftForm, setShiftForm] = useState({
    shiftId: '',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    skipWeekends: false,
    remarks: ''
  });
  const [groupForm, setGroupForm] = useState({ groupId: '', remarks: '' });
  const [bandForm, setBandForm] = useState({ bandId: '', remarks: '' });
  const [trainingForm, setTrainingForm] = useState({
    title: '',
    trainingType: 'INTERNAL',
    dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    startDate: dayjs().format('YYYY-MM-DD'),
    requireCertificate: false,
    remarks: ''
  });
  const [taskForm, setTaskForm] = useState({
    groupId: '',
    title: '',
    description: '',
    dueDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
    remarks: ''
  });

  const loadLookups = useCallback(async () => {
    try {
      const [bandRes, groupRes] = await Promise.all([
        axios.get('/api/hrms/bands', { params: { activeOnly: 'true' } }).catch(() => ({ data: [] })),
        axios.get('/api/hrms/groups', { params: { activeOnly: 'true' } }).catch(() => ({ data: [] }))
      ]);
      setBands(bandRes.data || []);
      setGroups(groupRes.data || []);
    } catch {
      setBands([]);
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e.department) set.add(e.department);
    });
    return [...set].sort();
  }, [employees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (deptFilter && e.department !== deptFilter) return false;
      if (!q) return true;
      const hay = [
        e.firstName,
        e.lastName,
        e.email,
        e.phone,
        e.role,
        roleLabel(e.role),
        e.department,
        ...(e.bands || []).map((b) => b.name),
        ...(e.groups || []).map((g) => g.name)
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [employees, search, deptFilter]);

  const departmentGroups = useMemo(() => {
    const byDept = new Map();
    for (const e of filtered) {
      const dept = e.department || 'UNASSIGNED';
      if (!byDept.has(dept)) byDept.set(dept, []);
      byDept.get(dept).push(e);
    }

    return [...byDept.entries()]
      .map(([department, people]) => {
        const byRole = new Map();
        for (const e of people) {
          const role = e.role || 'OTHER';
          if (!byRole.has(role)) byRole.set(role, []);
          byRole.get(role).push(e);
        }
        const roles = [...byRole.entries()]
          .map(([role, rolePeople]) => ({
            role,
            label: roleLabel(role),
            people: rolePeople.slice().sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        return {
          department,
          label: department === 'UNASSIGNED' ? 'No department' : department,
          theme: themeForDepartment(department === 'UNASSIGNED' ? null : department),
          people,
          roles
        };
      })
      .sort((a, b) => {
        if (a.department === 'UNASSIGNED') return 1;
        if (b.department === 'UNASSIGNED') return -1;
        return a.label.localeCompare(b.label);
      });
  }, [filtered]);

  const selectedEmployees = useMemo(
    () => filtered.filter((e) => selectedIds.has(String(e._id))),
    [filtered, selectedIds]
  );

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectPeople = (people) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const ids = people.map((p) => String(p._id));
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map((e) => String(e._id))));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const employeeGroups = useMemo(() => {
    if (!selectedTargets.length) return [];
    const sets = selectedTargets.map(
      (t) => new Set((t.groups || []).map((g) => String(g._id)))
    );
    let common = sets[0] || new Set();
    for (let i = 1; i < sets.length; i += 1) {
      common = new Set([...common].filter((id) => sets[i].has(id)));
    }
    return groups.filter((g) => common.has(String(g._id)) && g.isActive !== false);
  }, [selectedTargets, groups]);

  const openAction = (nextAction, employeesList) => {
    const list = Array.isArray(employeesList) ? employeesList.filter(Boolean) : [];
    if (!list.length) {
      showToast('Select at least one employee', 'error');
      return;
    }
    setSelectedTargets(list);
    setAction(nextAction);
    setShiftForm({
      shiftId: shifts[0]?._id || '',
      startDate: defaultFrom || dayjs().format('YYYY-MM-DD'),
      endDate: defaultFrom || dayjs().format('YYYY-MM-DD'),
      skipWeekends: false,
      remarks: ''
    });
    setGroupForm({ groupId: '', remarks: '' });
    setBandForm({ bandId: '', remarks: '' });
    setTrainingForm({
      title: '',
      trainingType: 'INTERNAL',
      dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
      startDate: dayjs().format('YYYY-MM-DD'),
      requireCertificate: false,
      remarks: ''
    });
    const groupIdSets = list.map(
      (t) => new Set((t.groups || []).map((g) => String(g._id)))
    );
    let common = groupIdSets[0] || new Set();
    for (let i = 1; i < groupIdSets.length; i += 1) {
      common = new Set([...common].filter((id) => groupIdSets[i].has(id)));
    }
    setTaskForm({
      groupId: [...common][0] || '',
      title: '',
      description: '',
      dueDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
      remarks: ''
    });
  };

  const closeAction = () => {
    setAction(null);
    setSelectedTargets([]);
  };

  const submit = async () => {
    if (!selectedTargets.length || !action) return;
    setSaving(true);
    try {
      const ids = selectedTargets.map((t) => t._id);
      if (action === 'shift') {
        if (!shiftForm.shiftId || !shiftForm.startDate || !shiftForm.endDate) {
          showToast('Shift and date range are required', 'error');
          return;
        }
        if (dayjs(shiftForm.endDate).isBefore(dayjs(shiftForm.startDate), 'day')) {
          showToast('End date must be on or after start date', 'error');
          return;
        }
        const results = await Promise.all(ids.map((userId) => axios.post('/api/hrms/roster', {
          userId,
          shiftId: shiftForm.shiftId,
          startDate: shiftForm.startDate,
          endDate: shiftForm.endDate,
          skipWeekends: shiftForm.skipWeekends,
          remarks: shiftForm.remarks || undefined
        })));
        const dayCount = results[0]?.data?.count || 1;
        showToast(
          dayCount > 1
            ? `Shift assigned to ${ids.length} employee(s) for ${dayCount} days`
            : `Shift assigned to ${ids.length} employee(s)`,
          'success'
        );
      } else if (action === 'group') {
        if (!groupForm.groupId) {
          showToast('Select a group', 'error');
          return;
        }
        await Promise.all(ids.map((userId) => axios.post(
          `/api/hrms/groups/${groupForm.groupId}/members`,
          { userId, remarks: groupForm.remarks || undefined }
        )));
        showToast(`Added ${ids.length} employee(s) to group`, 'success');
      } else if (action === 'band') {
        if (!bandForm.bandId) {
          showToast('Select a band', 'error');
          return;
        }
        await Promise.all(ids.map((userId) => axios.post(
          `/api/hrms/bands/${bandForm.bandId}/members`,
          { userId, remarks: bandForm.remarks || undefined }
        )));
        showToast(`Assigned ${ids.length} employee(s) to band`, 'success');
      } else if (action === 'training') {
        if (!trainingForm.title.trim() || !trainingForm.dueDate) {
          showToast('Title and due date are required', 'error');
          return;
        }
        await axios.post('/api/hrms/trainings', {
          title: trainingForm.title,
          trainingType: trainingForm.trainingType,
          dueDate: trainingForm.dueDate,
          startDate: trainingForm.startDate || undefined,
          requireCertificate: trainingForm.requireCertificate,
          remarks: trainingForm.remarks || undefined,
          userIds: ids
        });
        showToast(`Training assigned to ${ids.length} employee(s)`, 'success');
      } else if (action === 'task') {
        if (!taskForm.groupId || !taskForm.title.trim()) {
          showToast('Group and task title are required', 'error');
          return;
        }
        await axios.post(`/api/hrms/groups/${taskForm.groupId}/tasks`, {
          title: taskForm.title,
          description: taskForm.description || undefined,
          dueDate: taskForm.dueDate || undefined,
          remarks: taskForm.remarks || undefined,
          assigneeIds: ids
        });
        showToast(`Task assigned to ${ids.length} employee(s)`, 'success');
      }
      closeAction();
      clearSelection();
      await loadLookups();
      if (onChanged) onChanged();
    } catch (err) {
      showToast(err.response?.data?.message || 'Action failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const canDoAny = canAssign || canManageBands || canManageGroups || canAssignTraining;
  const selectionCount = selectedEmployees.length;

  const renderAssignActions = (e) => {
    if (!canDoAny) return null;
    const btnSx = {
      fontSize: '0.58rem',
      py: 0.1,
      px: 0.5,
      minWidth: 0,
      minHeight: 22,
      lineHeight: 1.1,
      '& .MuiButton-startIcon': { mr: 0.35, '& > *:nth-of-type(1)': { fontSize: 12 } }
    };
    return (
      <Stack direction="row" spacing={0.35} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {canAssign && (
          <Button size="small" startIcon={<Schedule />} sx={btnSx} onClick={() => openAction('shift', [e])}>
            Shift
          </Button>
        )}
        {canManageBands && (
          <Button size="small" startIcon={<WorkspacePremium />} sx={btnSx} onClick={() => openAction('band', [e])}>
            Band
          </Button>
        )}
        {canManageGroups && (
          <Button size="small" startIcon={<GroupsIcon />} sx={btnSx} onClick={() => openAction('group', [e])}>
            Group
          </Button>
        )}
        {canAssignTraining && (
          <Button size="small" startIcon={<School />} sx={btnSx} onClick={() => openAction('training', [e])}>
            Training
          </Button>
        )}
        {canManageGroups && (
          <Button
            size="small"
            startIcon={<Assignment />}
            sx={btnSx}
            onClick={() => openAction('task', [e])}
            disabled={!(e.groups || []).length}
            title={!(e.groups || []).length ? 'Add to a group first' : 'Assign group task'}
          >
            Task
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Employees are grouped by department (colored boxes). Same roles sit together inside each
        department with matching card colors. Multi-select cards for bulk assign.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search"
            placeholder="Name, email, phone, role, band, group…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Department</InputLabel>
            <Select
              label="Department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} employee{filtered.length === 1 ? '' : 's'} · {departmentGroups.length}{' '}
            department{departmentGroups.length === 1 ? '' : 's'}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1.5 }}>
          <Button size="small" onClick={selectAllFiltered} disabled={!filtered.length}>
            Select all visible
          </Button>
          <Button size="small" onClick={clearSelection} disabled={!selectionCount}>
            Clear selection
          </Button>
          <Typography variant="body2" color="text.secondary">
            {selectionCount} selected
          </Typography>
        </Stack>

        {selectionCount > 0 && canDoAny && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {canAssign && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Schedule />}
                onClick={() => openAction('shift', selectedEmployees)}
              >
                Assign shift
              </Button>
            )}
            {canManageBands && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<WorkspacePremium />}
                onClick={() => openAction('band', selectedEmployees)}
              >
                Assign band
              </Button>
            )}
            {canManageGroups && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<GroupsIcon />}
                onClick={() => openAction('group', selectedEmployees)}
              >
                Add to group
              </Button>
            )}
            {canAssignTraining && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<School />}
                onClick={() => openAction('training', selectedEmployees)}
              >
                Assign training
              </Button>
            )}
            {canManageGroups && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Assignment />}
                onClick={() => openAction('task', selectedEmployees)}
              >
                Assign task
              </Button>
            )}
          </Stack>
        )}
      </Paper>

      {!departmentGroups.length && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No employees match this filter</Typography>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {departmentGroups.map((dept) => {
          const deptIds = dept.people.map((p) => String(p._id));
          const selectedInDept = deptIds.filter((id) => selectedIds.has(id)).length;
          const allDeptSelected = deptIds.length > 0 && selectedInDept === deptIds.length;
          const someDeptSelected = selectedInDept > 0 && !allDeptSelected;
          const { theme } = dept;

          return (
            <Paper
              key={dept.department}
              variant="outlined"
              sx={{
                p: 1.2,
                borderColor: theme.border,
                borderWidth: 1.5,
                bgcolor: theme.bg
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1.2}
                flexWrap="wrap"
                gap={0.6}
              >
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Checkbox
                    size="small"
                    checked={allDeptSelected}
                    indeterminate={someDeptSelected}
                    onChange={() => selectPeople(dept.people)}
                    inputProps={{ 'aria-label': `Select all ${dept.label}` }}
                    sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                  />
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: theme.border,
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="h6" sx={{ fontSize: '0.72rem', fontWeight: 700, color: theme.header }}>
                    {dept.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${dept.people.length}`}
                    sx={{
                      bgcolor: theme.card,
                      color: theme.header,
                      height: 18,
                      fontSize: '0.65rem',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                </Stack>
              </Stack>

              <Stack spacing={1.2}>
                {dept.roles.map((roleGroup) => {
                  const roleIds = roleGroup.people.map((p) => String(p._id));
                  const selectedInRole = roleIds.filter((id) => selectedIds.has(id)).length;
                  const allRoleSelected = roleIds.length > 0 && selectedInRole === roleIds.length;
                  const someRoleSelected = selectedInRole > 0 && !allRoleSelected;

                  return (
                    <Box key={`${dept.department}-${roleGroup.role}`}>
                      <Stack direction="row" spacing={0.6} alignItems="center" mb={0.6}>
                        <Checkbox
                          size="small"
                          checked={allRoleSelected}
                          indeterminate={someRoleSelected}
                          onChange={() => selectPeople(roleGroup.people)}
                          inputProps={{ 'aria-label': `Select all ${roleGroup.label}` }}
                          sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: theme.header, fontSize: '0.68rem' }}
                        >
                          {roleGroup.label}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${roleGroup.people.length}`}
                          sx={{
                            borderColor: theme.border,
                            color: theme.header,
                            height: 18,
                            fontSize: '0.65rem',
                            '& .MuiChip-label': { px: 0.75 }
                          }}
                        />
                      </Stack>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(5, 1fr)',
                            lg: 'repeat(7, 1fr)'
                          },
                          gap: 0.9,
                          pl: { xs: 0, sm: 0.6 }
                        }}
                      >
                        {roleGroup.people.map((e) => {
                          const checked = selectedIds.has(String(e._id));
                          return (
                            <Box
                              key={e._id}
                              onClick={() => toggleSelect(e._id)}
                              sx={{
                                p: 0.9,
                                border: '1px solid',
                                borderColor: checked ? theme.header : theme.border,
                                borderRadius: 0.75,
                                bgcolor: theme.card,
                                opacity: checked ? 1 : 0.95,
                                boxShadow: checked ? `inset 0 0 0 1.5px ${theme.header}` : 'none',
                                minHeight: 72,
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer'
                              }}
                            >
                              <Stack direction="row" alignItems="flex-start" spacing={0.35}>
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onClick={(ev) => ev.stopPropagation()}
                                  onChange={() => toggleSelect(e._id)}
                                  sx={{ p: 0.15, mt: -0.2, '& .MuiSvgIcon-root': { fontSize: 15 } }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.2 }}
                                  >
                                    {nameOf(e)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ fontSize: '0.58rem', lineHeight: 1.2 }}
                                  >
                                    {e.email || '—'}
                                  </Typography>
                                  {e.phone ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ fontSize: '0.58rem', lineHeight: 1.2 }}
                                    >
                                      {e.phone}
                                    </Typography>
                                  ) : null}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.25, fontSize: '0.58rem', lineHeight: 1.2 }}
                                  >
                                    {e.manager
                                      ? `Mgr: ${`${e.manager.firstName || ''} ${e.manager.lastName || ''}`.trim()}`
                                      : 'No manager'}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={0.35} flexWrap="wrap" useFlexGap sx={{ mt: 0.4 }}>
                                {(e.bands || []).map((b) => (
                                  <Chip
                                    key={b._id}
                                    size="small"
                                    label={b.name}
                                    sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                                  />
                                ))}
                                {(e.groups || []).map((g) => (
                                  <Chip
                                    key={g._id}
                                    size="small"
                                    variant="outlined"
                                    label={g.name}
                                    sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                                  />
                                ))}
                              </Stack>
                              <Box sx={{ mt: 'auto' }} onClick={(ev) => ev.stopPropagation()}>
                                {renderAssignActions(e)}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Dialog open={Boolean(action)} onClose={closeAction} fullWidth maxWidth="sm">
        <DialogTitle>
          {action === 'shift' && 'Assign shift'}
          {action === 'group' && 'Add to group'}
          {action === 'band' && 'Assign band'}
          {action === 'training' && 'Assign training'}
          {action === 'task' && 'Assign group task'}
          {selectedTargets.length > 1
            ? ` · ${selectedTargets.length} employees`
            : (selectedTargets[0] ? ` · ${nameOf(selectedTargets[0])}` : '')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {selectedTargets.length > 1 && (
              <Alert severity="info">
                Applying to: {selectedTargets.map((t) => nameOf(t)).join(', ')}
              </Alert>
            )}
            {action === 'shift' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Shift</InputLabel>
                  <Select
                    label="Shift"
                    value={shiftForm.shiftId}
                    onChange={(e) => setShiftForm({ ...shiftForm, shiftId: e.target.value })}
                  >
                    {shifts.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name} ({s.startTime}–{s.endTime})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const d = dayjs().format('YYYY-MM-DD');
                      setShiftForm((f) => ({ ...f, startDate: d, endDate: d }));
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = dayjs();
                      setShiftForm((f) => ({
                        ...f,
                        startDate: today.startOf('week').format('YYYY-MM-DD'),
                        endDate: today.endOf('week').format('YYYY-MM-DD')
                      }));
                    }}
                  >
                    This week
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = dayjs();
                      setShiftForm((f) => ({
                        ...f,
                        startDate: today.startOf('month').format('YYYY-MM-DD'),
                        endDate: today.endOf('month').format('YYYY-MM-DD')
                      }));
                    }}
                  >
                    This month
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="From"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={shiftForm.startDate}
                    onChange={(e) => setShiftForm({
                      ...shiftForm,
                      startDate: e.target.value,
                      endDate: shiftForm.endDate < e.target.value ? e.target.value : shiftForm.endDate
                    })}
                    fullWidth
                  />
                  <TextField
                    label="To"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={shiftForm.endDate}
                    onChange={(e) => setShiftForm({ ...shiftForm, endDate: e.target.value })}
                    fullWidth
                  />
                </Stack>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={shiftForm.skipWeekends}
                      onChange={(e) => setShiftForm({ ...shiftForm, skipWeekends: e.target.checked })}
                    />
                  )}
                  label="Skip weekends (Sat–Sun)"
                />
                <TextField
                  label="Remarks"
                  value={shiftForm.remarks}
                  onChange={(e) => setShiftForm({ ...shiftForm, remarks: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {action === 'group' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Group</InputLabel>
                  <Select
                    label="Group"
                    value={groupForm.groupId}
                    onChange={(e) => setGroupForm({ ...groupForm, groupId: e.target.value })}
                  >
                    {groups.filter((g) => g.isActive !== false).map((g) => (
                      <MenuItem key={g._id} value={g._id}>
                        {g.name}{g.department ? ` · ${g.department}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Remarks"
                  value={groupForm.remarks}
                  onChange={(e) => setGroupForm({ ...groupForm, remarks: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {action === 'band' && (
              <>
                <Alert severity="info">
                  An employee can belong to only one band. Assigning moves them from any previous band.
                </Alert>
                <FormControl fullWidth>
                  <InputLabel>Band</InputLabel>
                  <Select
                    label="Band"
                    value={bandForm.bandId}
                    onChange={(e) => setBandForm({ ...bandForm, bandId: e.target.value })}
                  >
                    {bands.filter((b) => b.isActive !== false).map((b) => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.name}{b.department ? ` · ${b.department}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Remarks"
                  value={bandForm.remarks}
                  onChange={(e) => setBandForm({ ...bandForm, remarks: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {action === 'training' && (
              <>
                <TextField
                  label="Title"
                  required
                  fullWidth
                  value={trainingForm.title}
                  onChange={(e) => setTrainingForm({ ...trainingForm, title: e.target.value })}
                />
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={trainingForm.trainingType}
                    onChange={(e) => setTrainingForm({ ...trainingForm, trainingType: e.target.value })}
                  >
                    {['INTERNAL', 'EXTERNAL', 'MANDATORY', 'REFRESHER', 'OTHER'].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Start date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={trainingForm.startDate}
                  onChange={(e) => setTrainingForm({ ...trainingForm, startDate: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Due date"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={trainingForm.dueDate}
                  onChange={(e) => setTrainingForm({ ...trainingForm, dueDate: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Remarks"
                  value={trainingForm.remarks}
                  onChange={(e) => setTrainingForm({ ...trainingForm, remarks: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {action === 'task' && (
              <>
                {!employeeGroups.length ? (
                  <Alert severity="warning">
                    {selectedTargets.length > 1
                      ? 'Selected employees do not share a common group. Add them to the same group first, or select fewer people.'
                      : 'This employee is not in any group. Add them to a group first.'}
                  </Alert>
                ) : (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Group</InputLabel>
                      <Select
                        label="Group"
                        value={taskForm.groupId}
                        onChange={(e) => setTaskForm({ ...taskForm, groupId: e.target.value })}
                      >
                        {employeeGroups.map((g) => (
                          <MenuItem key={g._id} value={g._id}>
                            {g.name}{g.department ? ` · ${g.department}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Task title"
                      required
                      fullWidth
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    />
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      minRows={2}
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    />
                    <TextField
                      label="Due date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={taskForm.dueDate}
                      onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                      fullWidth
                />
                    <TextField
                      label="Remarks"
                      value={taskForm.remarks}
                      onChange={(e) => setTaskForm({ ...taskForm, remarks: e.target.value })}
                      fullWidth
                    />
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || (action === 'task' && !employeeGroups.length)}
          >
            {saving
              ? 'Saving…'
              : (selectedTargets.length > 1 ? `Assign (${selectedTargets.length})` : 'Assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeesGrid;
